import type { TaskListListOutput } from "@orsane/schema/task-lists";
import type { Priority, ShortTaskOutput, TaskStatusOutput } from "@orsane/schema/tasks";
import { useSuspenseQueries } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { format, isPast } from "date-fns";
import {
    Calendar,
    ChevronDown,
    ChevronUp,
    CircleDashed,
    ClipboardCopy,
    Ellipsis,
    Flag,
    FolderTree,
    Link as LinkIcon,
    ListTree,
    Logs,
    MessageCircle,
    ReceiptText,
    SquareArrowOutUpRight,
    Trash2,
    Users,
    X,
} from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "usehooks-ts";
import { useLocalSettingsStore } from "@/common/stores";
import { useFeatures } from "@/contexts/FeaturesContext";
import { useTaskNavigation } from "@/hooks/useTaskNavigation";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
    useChangeTaskAssigneesMutation,
    useChangeTaskParentMutation,
    useChangeTaskStatusMutation,
    useDeleteTaskMutation,
    useUpdateListsOfTaskMutation,
    useUpdateTaskPropertiesMutation,
} from "@/mutations/task-mutations";
import { useActivityCount, useSubTasksCount } from "@/queries/task-queries";
import { AlertDialogType } from "@/types/alert-dialog";
import Chat from "../chat/Chat";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import DueDateSelect from "../property-selectors/DueDateSelect";
import { ListSelect } from "../property-selectors/ListSelect";
import { PrioritySelect } from "../property-selectors/PrioritySelect";
import StatusSelect from "../property-selectors/StatusSelect";
import { TaskSelect } from "../property-selectors/TaskSelect";
import { UserSelect } from "../property-selectors/UserSelect";
import ScrollContainer from "../ScrollContainer";
import DescriptionEditor from "../text-editor/DescriptionEditor";
import { Button } from "../ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTab } from "../ui/tabs";
import { CustomAttributeValueDescriptionListItem } from "./CustomAttributeValueDescriptionListItem";
import { DescriptionList, DescriptionListItem, DescriptionListItemButton } from "./DescriptionList";
import SubTasks from "./SubTasks";
import TitleEdit from "./TitleEdit";

function EmptyToken() {
    return <span className="text-neutral-400">—</span>;
}

function TabBadge({ count }: { count: number | undefined }) {
    if (!count) return null;

    return (
        <div className="bg-sky-200 text-xs ms-1.5 px-1.5 leading-4 text-sky-800 py-0 rounded-md grid place-items-center">
            {count}
        </div>
    );
}

export default function TaskForm({
    taskId,
    showCloseButton = true,
    onClose,
}: {
    taskId: number;
    showCloseButton?: boolean;
    onClose?: () => void;
}) {
    const { hasFeature } = useFeatures();
    const { formatDate } = useDateFormatter();
    const { formAttributesCollapsiblePanelOpen, setFormAttributesCollapsiblePanelOpen } = useLocalSettingsStore();
    const { data: activityCount } = useActivityCount({ id: taskId });
    const { data: subTasksCount } = useSubTasksCount({ id: taskId });
    const trpc = useTRPC();
    const deleteTaskAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const { mutate: mutateChangeTaskStatus } = useChangeTaskStatusMutation();
    const { openTask: navigateToTask } = useTaskNavigation();

    // queries
    const getTaskOpts = trpc.task.get.queryOptions({ id: taskId });
    const getAttributeOpts = trpc.customAttributes.list.queryOptions(undefined, {
        staleTime: Infinity,
        gcTime: Infinity,
    });
    const [{ data: task }, { data: customAttributes }] = useSuspenseQueries({
        queries: [getTaskOpts, getAttributeOpts],
    });
    useDocumentTitle(`T-${task.number} ${task.title} - Orsane`, { preserveTitleOnUnmount: false });

    const tab = useSearch({ strict: false, select: (s: any) => s.tab as string });

    const currentTab = useMemo(() => {
        return tab || "description";
    }, [tab]);

    // mutations
    const { mutate: mutateUpdateTaskProperties } = useUpdateTaskPropertiesMutation();
    const deleteMutation = useDeleteTaskMutation();
    const { mutate: mutateUpdateListsOfTask } = useUpdateListsOfTaskMutation();
    const { mutate: mutateUpdateAssignees } = useChangeTaskAssigneesMutation();
    const { mutate: mutateUpdateParentTask } = useChangeTaskParentMutation();

    const handleTitleChange = (value: string) => {
        const isDirty = task?.title !== value;
        if (isDirty) {
            mutateUpdateTaskProperties({
                id: task.id,
                updates: { title: value },
            });
        }
    };

    const handleStatusChange = (_statusId: number, status: TaskStatusOutput) => {
        mutateChangeTaskStatus({
            id: taskId,
            taskStatus: status,
        });
    };

    const handlePriorityChange = (priority: Priority | null) => {
        mutateUpdateTaskProperties({
            id: task.id,
            updates: { priority },
        });
    };

    const handleParentChange = (parentTask: ShortTaskOutput | null) => {
        mutateUpdateParentTask({
            id: task.id,
            parentTask,
        });
    };

    const handleDueDateChange = (value: Date | null) => {
        mutateUpdateTaskProperties({
            id: task.id,
            updates: { dueDate: value ? format(value, "yyyy-MM-dd") : null },
        });
    };

    const handleListsChange = (lists: TaskListListOutput[]) => {
        mutateUpdateListsOfTask({ id: task.id, taskLists: lists });
    };

    const handleDeleteTask = async () => {
        if (
            await deleteTaskAlertDialogRef.current?.show({
                description: `Are you sure you want to delete task T-${task.number}? This action cannot be undone.`,
            })
        ) {
            onClose?.();
            await deleteMutation.mutateAsync({ id: taskId });
        }
    };

    const handleCopyTaskId = () => {
        const str = `T-${task.number}`;
        navigator.clipboard.writeText(str);
        toast.success("Task ID copied", {
            description: `The task ID has been copied to your clipboard.`,
        });
    };

    const handleCopyTaskTitle = () => {
        const str = `T-${task.number} ${task.title}`;
        navigator.clipboard.writeText(str);
        toast.success("Task title copied", {
            description: `The task title has been copied to your clipboard.`,
        });
    };

    const handleCopyTaskLink = () => {
        const tenantBaseUrl = window.location.origin;
        const url = `${tenantBaseUrl}/all?task=${taskId}`;
        navigator.clipboard.writeText(url);
        toast.success("Task link copied", {
            description: `The link to this task has been copied to your clipboard.`,
        });
    };

    const handleOpenParentTask = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (task.parentTask) {
                navigateToTask(task.parentTask.id);
            }
        },
        [task.parentTask, navigateToTask]
    );

    return (
        <section className="flex flex-col justify-between h-full gap-1.5 bg-white rounded-r-sm">
            <div className="flex items-center gap-2 justify-between p-4 pb-0">
                <div className="flex-1 flex items-center gap-4">
                    <div className="text-lg font-medium text-orsane-muted text-nowrap">T-{task.number}</div>
                    {/* <InvisibleAutogrowInput value={task.title} onChange={handleTitleChange} className="text-lg font-medium px-2 py-1 -ml-2" /> */}
                    <TitleEdit value={task.title} onChange={handleTitleChange} />
                </div>
                <div className="flex items-top self-start">
                    {/* <Tooltip>
                            <TooltipTrigger render>
                                <Button variant="ghost" size="icon" className="text-zinc-600 hover:text-zinc-900">
                                    <LinkIcon className="size-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Copy link to task</TooltipContent>
                        </Tooltip> */}
                    {/* <Tooltip>
                            <TooltipTrigger render>
                                <Button variant="ghost" size="icon" className="text-zinc-600 hover:text-zinc-900">
                                    <Bell className="size-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Watch this task</TooltipContent>
                        </Tooltip> */}
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-zinc-600 hover:text-zinc-900 hover:bg-neutral-200/70"
                                >
                                    <Ellipsis className="size-4" />
                                </Button>
                            }
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent>
                            {/* <DropdownMenuItem>
                                    <Files />
                                    Duplicate task
                                </DropdownMenuItem> */}
                            <DropdownMenuItem onClick={handleCopyTaskId}>
                                <ClipboardCopy />
                                Copy task ID
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleCopyTaskTitle}>
                                <ClipboardCopy />
                                Copy task title
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleCopyTaskLink}>
                                <LinkIcon />
                                Copy task link
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleDeleteTask}>
                                <Trash2 /> Delete task
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {showCloseButton && (
                        <div className="ml-0.5 relative before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-px before:bg-neutral-200/60">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-zinc-600 hover:bg-neutral-200/70"
                                onClick={onClose}
                            >
                                <X className="size-4" />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <div className="p-4">
                <DescriptionList>
                    <DescriptionListItem label="Status" icon={<CircleDashed />}>
                        <StatusSelect
                            trigger={(content) => (
                                <DescriptionListItemButton>
                                    <div>{content}</div>
                                </DescriptionListItemButton>
                            )}
                            hideTodo={false}
                            value={task.taskStatusId}
                            onChange={handleStatusChange}
                        ></StatusSelect>
                    </DescriptionListItem>
                    <DescriptionListItem label="Priority" icon={<Flag className="size-3.5" />}>
                        <PrioritySelect
                            trigger={(content) => (
                                <DescriptionListItemButton className={task.priority === "urgent" ? "text-red-500" : ""}>
                                    <div className="flex items-center gap-1.5">{content}</div>
                                </DescriptionListItemButton>
                            )}
                            value={task.priority}
                            onChange={handlePriorityChange}
                        ></PrioritySelect>
                    </DescriptionListItem>
                    <DescriptionListItem label="Due" icon={<Calendar className="size-3.5" />}>
                        <DueDateSelect
                            trigger={(value) => (
                                <DescriptionListItemButton
                                    className={cn({
                                        "text-orsane-overdue": !!value && isPast(value),
                                    })}
                                >
                                    {value ? formatDate(value) : "No due date"}
                                </DescriptionListItemButton>
                            )}
                            value={task.dueDate}
                            onChange={handleDueDateChange}
                        ></DueDateSelect>
                    </DescriptionListItem>
                    <DescriptionListItem label="Lists" icon={<Logs className="size-3.5" />}>
                        <ListSelect
                            trigger={(content, value) => (
                                <DescriptionListItemButton width="full" className="overflow-x-hidden w-full">
                                    {value?.length > 0 && (
                                        <div className="col-span-2 flex w-full items-center gap-1 overflow-hidden mask-linear-90 mask-linear-from-[calc(100%-20px)]">
                                            {content}
                                        </div>
                                    )}
                                    {value?.length === 0 && <EmptyToken />}
                                </DescriptionListItemButton>
                            )}
                            value={task.taskLists}
                            onChange={handleListsChange}
                        ></ListSelect>
                    </DescriptionListItem>
                    <DescriptionListItem label="Assignee" icon={<Users className="size-3.5" />}>
                        <UserSelect
                            trigger={(content, value) => (
                                <DescriptionListItemButton>
                                    <div className="col-span-2">{value?.length > 0 ? content : <EmptyToken />}</div>
                                </DescriptionListItemButton>
                            )}
                            value={task.assignees}
                            onChange={(v) => {
                                mutateUpdateAssignees({
                                    id: task.id,
                                    assignees: v,
                                });
                            }}
                        />
                    </DescriptionListItem>
                    {hasFeature("subtasks") && (
                        <DescriptionListItem label="Parent" icon={<FolderTree className="size-3.5" />}>
                            <div className="inline-flex w-auto items-center gap-0">
                                <TaskSelect
                                    ignoreId={task.id}
                                    trigger={(content, _value) => (
                                        <DescriptionListItemButton>
                                            <div className="col-span-2 flex items-center gap-2">
                                                {content ? content : <EmptyToken />}
                                            </div>
                                        </DescriptionListItemButton>
                                    )}
                                    value={task.parentTask}
                                    onChange={(v) => {
                                        handleParentChange(v);
                                    }}
                                />
                                {task.parentTask && (
                                    <button
                                        type="button"
                                        className="size-6 grid place-items-center duration-150 cursor-pointer text-orsane-muted hover:text-orsane-primary appearance-none p-1 pr-0"
                                        onClick={handleOpenParentTask}
                                    >
                                        <SquareArrowOutUpRight className="size-3.5" />
                                    </button>
                                )}
                            </div>
                        </DescriptionListItem>
                    )}

                    {customAttributes.length > 0 && (
                        <Collapsible
                            open={formAttributesCollapsiblePanelOpen}
                            onOpenChange={setFormAttributesCollapsiblePanelOpen}
                        >
                            <CollapsibleContent className="flex [&[hidden]:not([hidden='until-found'])]:hidden h-(--collapsible-panel-height) flex-col justify-end overflow-hidden text-sm transition-all ease-out data-ending-style:h-0 data-starting-style:h-0 duration-150">
                                {customAttributes.map((attribute) => (
                                    <CustomAttributeValueDescriptionListItem
                                        key={attribute.id}
                                        task={task}
                                        attribute={attribute}
                                    />
                                ))}
                            </CollapsibleContent>
                            <CollapsibleTrigger
                                className="grid place-items-center mx-auto mt-2 group/collapsible-trigger "
                                render={
                                    <Button variant="ghost" size="sm">
                                        {formAttributesCollapsiblePanelOpen ? (
                                            <div className="flex items-center text-sm gap-1 text-orsane-muted">
                                                Show less
                                                <ChevronUp className=" size-4 group-hover/collapsible-trigger:text-orsane-muted" />
                                            </div>
                                        ) : (
                                            <div className="flex items-center text-sm gap-1 text-orsane-muted">
                                                Show more
                                                <ChevronDown className=" size-4 group-hover/collapsible-trigger:text-orsane-muted" />
                                            </div>
                                        )}
                                    </Button>
                                }
                            ></CollapsibleTrigger>
                        </Collapsible>
                    )}
                </DescriptionList>
            </div>

            <div className="flex-1 relative">
                <Tabs className="w-full h-full" value={currentTab}>
                    <div className="border-b pl-4 ">
                        <TabsList variant="underline">
                            <TabsTab
                                nativeButton={false}
                                value="description"
                                render={
                                    <Link
                                        to="."
                                        search={(prev: any) => ({
                                            ...prev,
                                            tab: "description",
                                        })}
                                    >
                                        <ReceiptText />
                                        Description
                                    </Link>
                                }
                            />
                            <TabsTab
                                nativeButton={false}
                                value="activity"
                                render={
                                    <Link
                                        to="."
                                        search={(prev: any) => ({
                                            ...prev,
                                            tab: "activity",
                                        })}
                                    >
                                        <MessageCircle />
                                        Activity
                                        <TabBadge count={activityCount} />
                                    </Link>
                                }
                            />
                            {hasFeature("subtasks") && (
                                <TabsTab
                                    nativeButton={false}
                                    value="subtasks"
                                    render={
                                        <Link
                                            to="."
                                            search={(prev: any) => ({
                                                ...prev,
                                                tab: "subtasks",
                                            })}
                                        >
                                            <ListTree />
                                            Subtasks
                                            <TabBadge count={subTasksCount} />
                                        </Link>
                                    }
                                />
                            )}
                        </TabsList>
                    </div>
                    <TabsContent value="description" className="w-full relative">
                        <div className="absolute inset-0 overflow-hidden bg-white rounded-r-sm">
                            <ScrollContainer className="h-full w-full overflow-scroll p-4">
                                <div className="h-full">
                                    <DescriptionEditor key={taskId} taskId={taskId} />
                                </div>
                            </ScrollContainer>
                        </div>
                    </TabsContent>
                    <TabsContent value="activity" className="flex flex-col">
                        <div className="flex flex-col h-full inset-0 overflow-hidden">
                            <Chat key={taskId} taskId={taskId} />
                        </div>
                    </TabsContent>
                    {hasFeature("subtasks") && (
                        <TabsContent value="subtasks" className="flex flex-col">
                            <SubTasks taskId={taskId} parentTask={task} />
                        </TabsContent>
                    )}
                </Tabs>
            </div>
            <DefaultAlertDialog
                ref={deleteTaskAlertDialogRef}
                type={AlertDialogType.Delete}
                title="Delete task"
                description="Are you sure you want to delete this task? This action cannot be undone."
            />
        </section>
    );
}
